"use strict";
(() => {
  if (customElements.get("pb-tab")) return;

  class PbTab extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        trigger: null,
        content: null,
        contentMb: null,
      };
      this._isOpen = false;
      this._placeholder = null;
    }

    connectedCallback() {
      this.setupElements();
      this.setupA11y();
      this.addEventListeners();
      this.handleResponsiveInit(); // 👈 check width on init
    }

    disconnectedCallback() {
      this.removeEventListeners();
      this.removeResizeListener(); // 👈 cleanup
      if (this._isOpen) this.restoreContent();
    }
  }

  // --- Setup ---
  PbTab.prototype.setupElements = function () {
    this.elements.trigger = this.querySelector('[data-component="trigger"]');
    this.elements.content = this.querySelector('[data-component="content"]');

    if (this.elements.content) {
      this.elements.content.classList.add("pb-tab__content");
    }

    const tabsRoot = this.closest(".pb-tabs");
    this.elements.contentMb =
      tabsRoot?.querySelector('[data-component="content-mb"]') || null;

    if (!this._placeholder) {
      this._placeholder = document.createComment("pb-tab-content-placeholder");
      if (this.elements.content && this.elements.content.parentNode) {
        this.elements.content.parentNode.insertBefore(
          this._placeholder,
          this.elements.content,
        );
      }
    }
  };

  PbTab.prototype.setupA11y = function () {
    const t = this.elements.trigger;
    const c = this.elements.content;

    if (t) {
      t.setAttribute("role", "tab");
      t.setAttribute("tabindex", t.getAttribute("tabindex") ?? "0");
      const triggerId =
        t.id || `pb-tab-trigger-${Math.random().toString(36).slice(2)}`;
      t.id = triggerId;
    }

    if (c) {
      const panelId =
        c.id || `pb-tab-panel-${Math.random().toString(36).slice(2)}`;
      c.id = panelId;
      c.setAttribute("role", "tabpanel");
      c.setAttribute("aria-labelledby", this.elements.trigger?.id || "");
    }

    this.updateA11y();
  };

  PbTab.prototype.addEventListeners = function () {
    if (!this.elements.trigger) return;
    this._boundClick = this.onClick.bind(this);
    this._boundKeydown = this.onKeydown.bind(this);
    this.elements.trigger.addEventListener("click", this._boundClick);
    this.elements.trigger.addEventListener("keydown", this._boundKeydown);
  };

  PbTab.prototype.removeEventListeners = function () {
    if (!this.elements.trigger) return;
    this.elements.trigger.removeEventListener("click", this._boundClick);
    this.elements.trigger.removeEventListener("keydown", this._boundKeydown);
  };

  // --- Accordion logic ---
  PbTab.prototype.open = function () {
    if (this._isOpen) return;
    this.closeSiblings();
    this.mountInMb();
    this._isOpen = true;
    this.classList.add("is-open");
    this.elements.content?.classList.add("pb-tab__content--active");
    this.updateA11y();
  };

  PbTab.prototype._clearMb = function () {
    const { contentMb } = this.elements || {};
    if (contentMb) contentMb.innerHTML = "";
  };

  PbTab.prototype.close = function () {
    if (!this._isOpen) return;
    this._clearMb();
    this.restoreContent();
    this._isOpen = false;
    this.classList.remove("is-open");
    this.elements.content?.classList.remove("pb-tab__content--active");
    this.updateA11y();
  };

  PbTab.prototype.toggle = function () {
    this._isOpen ? this.close() : this.open();
  };

  PbTab.prototype.mountInMb = function () {
    const { content, contentMb } = this.elements;
    if (!content) return;
    if (contentMb) {
      contentMb.innerHTML = content.innerHTML;
    }
  };

  PbTab.prototype.restoreContent = function () {
    const { content } = this.elements;
    if (!content || !this._placeholder || !this._placeholder.parentNode) return;
    if (content.previousSibling === this._placeholder) return;
    this._placeholder.parentNode.insertBefore(
      content,
      this._placeholder.nextSibling,
    );
  };

  PbTab.prototype.closeSiblings = function () {
    const root = this.closest("pb-tabs") || this.parentElement;
    if (!root) return;
    root.querySelectorAll("pb-tab").forEach((tab) => {
      if (tab !== this && typeof tab.close === "function") tab.close();
    });
  };

  PbTab.prototype.updateA11y = function () {
    const t = this.elements.trigger;
    const c = this.elements.content;
    if (t) {
      t.setAttribute("aria-selected", this._isOpen ? "true" : "false");
      t.setAttribute("aria-expanded", this._isOpen ? "true" : "false");
      t.setAttribute("aria-controls", c?.id || "");
    }
    if (c) {
      c.setAttribute("aria-hidden", this._isOpen ? "false" : "true");
    }
  };

  // --- Handlers ---
  PbTab.prototype.onClick = function () {
    this.toggle();
  };

  PbTab.prototype.onKeydown = function (e) {
    const key = e.key;
    if (key === "Enter" || key === " ") {
      e.preventDefault();
      this.toggle();
      return;
    }

    const allTabs = Array.from(
      (this.closest("pb-tabs") || this.parentElement)?.querySelectorAll(
        "pb-tab",
      ) || [],
    );
    const triggers = allTabs.map((t) => t.elements?.trigger).filter(Boolean);
    const currentIndex = triggers.indexOf(this.elements.trigger);
    if (currentIndex < 0) return;

    const focusAt = (idx) => {
      const trg = triggers[idx];
      if (trg) trg.focus();
    };

    if (key === "ArrowDown" || key === "ArrowRight") {
      e.preventDefault();
      focusAt((currentIndex + 1) % triggers.length);
    } else if (key === "ArrowUp" || key === "ArrowLeft") {
      e.preventDefault();
      focusAt((currentIndex - 1 + triggers.length) % triggers.length);
    } else if (key === "Home") {
      e.preventDefault();
      focusAt(0);
    } else if (key === "End") {
      e.preventDefault();
      focusAt(triggers.length - 1);
    }
  };

  // --- Responsive Behavior ---
  PbTab.prototype.isMobile = function () {
    return (window.innerWidth || document.documentElement.clientWidth) < 769;
  };

  PbTab.prototype.handleResponsiveInit = function () {
    // Add resize listener
    this._boundResize = this.handleResize.bind(this);
    window.addEventListener("resize", this._boundResize);

    // On init
    if (this.isMobile()) {
      this.activateFirstTab();
    }
  };

  PbTab.prototype.handleResize = function () {
    const nowMobile = this.isMobile();
    if (nowMobile && !this._wasMobile) {
      this.activateFirstTab();
    }
    this._wasMobile = nowMobile;
  };

  PbTab.prototype.activateFirstTab = function () {
    const root = this.closest("pb-tabs") || document;
    const firstTab = root.querySelector("pb-tab");
    if (firstTab && typeof firstTab.open === "function") {
      firstTab.open();
    }
  };

  PbTab.prototype.removeResizeListener = function () {
    if (this._boundResize) {
      window.removeEventListener("resize", this._boundResize);
    }
  };

  customElements.define("pb-tab", PbTab);
})();

